import { useEffect, useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  Settings,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  AudioLines,
  RotateCcw,
  Check,
  Play,
  Square,
  Loader2,
  Lock,
} from "lucide-react";
import {
  fetchProjectProfile,
  generateScenarios,
  fetchProjectAudio,
  saveAudioSelection,
  saveVoiceInstructions,
  fetchVoicePreview,
  fetchSelectedScenario,
  type ScenarioSpec,
} from "@/api/client";
import axios from "axios";
import { useSessionStore } from "@/hooks/useSessionStore";
import { ScenarioGenerationPopup } from "@/components/ScenarioGenerationPopup";
import { useQueryClient } from "@tanstack/react-query";
import { ScenarioItemCard, type ScenarioItem } from "@/components/scenario-config/ScenarioItemCard";
import { RegenerateScenariosDialog } from "@/components/scenario-config/RegenerateScenariosDialog";
import { ELEVEN_LABS_VOICES } from "@/constants/voices";

type AiProvider = "eleven_labs" | "qwen_local";

const SCENARIO_CONFIG_STORAGE_PREFIX = "scenarioConfig:";

function makeDefaultScenario(): ScenarioItem {
  return {
    id: 1,
    title: "Scénario 1",
    isOpen: false,
    aiProvider: "eleven_labs",
    prompt: "",
    targetAudience: "Sélectionnez",
    narrativeTone: "Sélectionnez",
    sourceMatch: 50,
    durationSeconds: 120,
    ttsVoiceId: ELEVEN_LABS_VOICES[0].id,
  };
}

function loadScenarioConfig(sessionId?: string): { scenarios: ScenarioItem[]; voiceInstructions: string } | null {
  if (!sessionId || typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(SCENARIO_CONFIG_STORAGE_PREFIX + sessionId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.scenarios) || parsed.scenarios.length === 0) return null;
    return {
      scenarios: parsed.scenarios.map((s: ScenarioItem) => ({ ...s, isOpen: false })),
      voiceInstructions: typeof parsed.voiceInstructions === "string" ? parsed.voiceInstructions : "",
    };
  } catch {
    return null;
  }
}

function saveScenarioConfig(sessionId: string, scenarios: ScenarioItem[], voiceInstructions: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      SCENARIO_CONFIG_STORAGE_PREFIX + sessionId,
      JSON.stringify({ scenarios, voiceInstructions })
    );
  } catch {
    /* ignore quota errors */
  }
}

export function ConfigurationScenarioView() {
  const navigate = useNavigate();
  const { projectName, lastProjectName, setProjectName, sessionId, setScenarioTarget, updateProgress } = useSessionStore();
  const queryClient = useQueryClient();
  const resolvedProjectName = projectName ?? lastProjectName;
  const [showAiDropdownId, setShowAiDropdownId] = useState<number | null>(null);
  const [showAudienceDropdownId, setShowAudienceDropdownId] = useState<number | null>(null);
  const [showToneDropdownId, setShowToneDropdownId] = useState<number | null>(null);
  const [showVoiceDropdownId, setShowVoiceDropdownId] = useState<number | null>(null);
  const [showGenerationPopup, setShowGenerationPopup] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const forcedRunRef = useRef(false);
  const [voiceInstructions, setVoiceInstructions] = useState(
    () => loadScenarioConfig(sessionId)?.voiceInstructions ?? ""
  );
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [loadingPreviewId, setLoadingPreviewId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const handlePlayPreview = async (e: React.MouseEvent, voiceId: string) => {
    e.stopPropagation();
    const wasPlayingThis = playingVoiceId === voiceId || loadingPreviewId === voiceId;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPlayingVoiceId(null);
    setLoadingPreviewId(null);
    if (wasPlayingThis) return;
    setLoadingPreviewId(voiceId);
    try {
      const blob = await fetchVoicePreview(voiceId);
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setPlayingVoiceId(null);
        audioRef.current = null;
        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
          previewUrlRef.current = null;
        }
      };
      setLoadingPreviewId(null);
      setPlayingVoiceId(voiceId);
      audio.play();
    } catch {
      setLoadingPreviewId(null);
      setPlayingVoiceId(null);
    }
  };
  const [scenarios, setScenarios] = useState<ScenarioItem[]>(
    () => loadScenarioConfig(sessionId)?.scenarios ?? [makeDefaultScenario()]
  );
  const maxChars = 3000;
  const aiOptions = [
    {
      id: "eleven_labs" as const,
      label: "Eleven Labs",
      description: "Voix expressives, qualité élevée. Échantillon généré après le choix.",
    },
    {
      id: "qwen_local" as const,
      label: "Qwen local",
      description: "Plus de personnalisation, échantillon généré en fin d'artefact.",
    },
  ];
  const projectProfileQuery = useQuery({
    queryKey: ["configuration-project-profile", resolvedProjectName],
    queryFn: () => fetchProjectProfile(resolvedProjectName!),
    enabled: Boolean(resolvedProjectName),
  });
  // Le verrou est propre à l'artefact (la session) : il existe dès qu'un scénario
  // y a été retenu. Les autres artefacts du projet ne sont pas concernés.
  const selectedScenarioQuery = useQuery({
    queryKey: ["selected-scenario", sessionId],
    queryFn: () => fetchSelectedScenario(sessionId!),
    enabled: Boolean(sessionId),
  });
  const isLocked = Boolean(selectedScenarioQuery.data);
  const selectedScenarioTitle = extractScenarioTitle(selectedScenarioQuery.data);
  const audienceOptions = projectProfileQuery.data?.preference_options?.audience_options ?? [];
  const toneOptions = projectProfileQuery.data?.preference_options?.tone_options ?? [];

  useEffect(() => {
    if (!projectName && lastProjectName) {
      setProjectName(lastProjectName);
    }
  }, [projectName, lastProjectName, setProjectName]);

  // Restaure la config persistée quand la session change (après mount ; les valeurs
  // initiales sont déjà restaurées via les initialiseurs useState paresseux).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const persisted = loadScenarioConfig(sessionId);
    setScenarios(persisted?.scenarios ?? [makeDefaultScenario()]);
    setVoiceInstructions(persisted?.voiceInstructions ?? "");
  }, [sessionId]);

  // Persiste la config (prompts inclus) pour survivre à la navigation aller/retour.
  const persistedSessionRef = useRef(sessionId);
  useEffect(() => {
    if (!sessionId) return;
    if (persistedSessionRef.current !== sessionId) {
      // sessionId vient de changer ; l'effet de restauration repeuple l'état —
      // on saute ce commit pour ne pas écrire les données de la session précédente.
      persistedSessionRef.current = sessionId;
      return;
    }
    saveScenarioConfig(sessionId, scenarios, voiceInstructions);
  }, [sessionId, scenarios, voiceInstructions]);

  // Keep window.__scenarioPrompts in sync so ChatPanel can read prompt values
  useEffect(() => {
    (window as Window & { __scenarioPrompts?: string[] }).__scenarioPrompts = scenarios.map((s) => s.prompt);
    return () => {
      (window as Window & { __scenarioPrompts?: string[] }).__scenarioPrompts = undefined;
    };
  }, [scenarios]);

  // Listen for scenario-prompt-updated events dispatched by the chat agent tool
  useEffect(() => {
    const handler = (e: Event) => {
      const { scenario_index, action, content } = (e as CustomEvent<{ scenario_index: number; action: "replace" | "append"; content: string }>).detail;
      const idx = scenario_index ?? 0;
      setScenarios((prev) =>
        prev.map((s, i) =>
          i === idx
            ? { ...s, prompt: action === "replace" ? content : s.prompt + (s.prompt ? "\n" : "") + content }
            : s
        )
      );
    };
    window.addEventListener("scenario-prompt-updated", handler);
    return () => window.removeEventListener("scenario-prompt-updated", handler);
  }, []);

  // Listen for voice-selected events dispatched by the chat agent (select_voice tool)
  useEffect(() => {
    const handler = (e: Event) => {
      const { voice_id } = (e as CustomEvent<{ voice_id: string; voice_label: string }>).detail;
      if (!voice_id) return;
      // Apply to the first open scenario, or to all if none is open
      setScenarios((prev) => {
        const openIdx = prev.findIndex((s) => s.isOpen);
        return prev.map((s, i) =>
          (openIdx === -1 || i === openIdx) ? { ...s, ttsVoiceId: voice_id } : s
        );
      });
    };
    window.addEventListener("voice-selected", handler);
    return () => window.removeEventListener("voice-selected", handler);
  }, []);

  useEffect(() => {
    if (!projectProfileQuery.data) return;
    const selectedAudience = projectProfileQuery.data.audience ?? audienceOptions[0] ?? "Sélectionnez";
    const selectedTone = projectProfileQuery.data.tone ?? toneOptions[0] ?? "Sélectionnez";
    setScenarios((prev) =>
      prev.map((scenario) => ({
        ...scenario,
        targetAudience:
          scenario.targetAudience && scenario.targetAudience !== "Sélectionnez"
            ? scenario.targetAudience
            : formatOptionLabel(selectedAudience),
        narrativeTone:
          scenario.narrativeTone && scenario.narrativeTone !== "Sélectionnez"
            ? scenario.narrativeTone
            : formatOptionLabel(selectedTone),
      }))
    );
    const vi = projectProfileQuery.data.voice_instructions;
    if (vi) {
      setVoiceInstructions((prev) => prev || vi);
    }
  }, [projectProfileQuery.data, audienceOptions, toneOptions]);

  const displayAudienceOptions =
    audienceOptions.length > 0 ? audienceOptions.map(formatOptionLabel) : ["Sélectionnez"];
  const displayToneOptions =
    toneOptions.length > 0 ? toneOptions.map(formatOptionLabel) : ["Sélectionnez"];

  const updateScenario = (id: number, patch: Partial<ScenarioItem>) => {
    setScenarios((prev) => prev.map((scenario) => (scenario.id === id ? { ...scenario, ...patch } : scenario)));
  };

  const handleAddScenario = () => {
    if (scenarios.length >= 3) return;
    const firstScenario = scenarios[0];
    const nextNumber = scenarios.length + 1;
    const newScenario: ScenarioItem = {
      ...firstScenario,
      id: Date.now(),
      title: `Scénario ${nextNumber}`,
      isOpen: false,
    };
    setScenarios((prev) => [...prev, newScenario]);
  };

  const handleDeleteScenario = (id: number) => {
    if (scenarios.length <= 1) return;
    setScenarios((prev) => prev.filter((scenario) => scenario.id !== id));
    if (showAiDropdownId === id) setShowAiDropdownId(null);
    if (showAudienceDropdownId === id) setShowAudienceDropdownId(null);
    if (showToneDropdownId === id) setShowToneDropdownId(null);
    if (showVoiceDropdownId === id) setShowVoiceDropdownId(null);
  };

  const reverseFormatLabel = (label: string, options: string[]) => {
    if (!label || label === "Sélectionnez") return undefined;
    const match = options.find((opt) => formatOptionLabel(opt) === label);
    return match ?? label.toLowerCase().replace(/\s+/g, "_");
  };

  const sourceUsageFromMatch = (value: number): "leger" | "modere" | "central" => {
    if (value <= 33) return "leger";
    if (value <= 66) return "modere";
    return "central";
  };

  const sourceMatchLabel = (value: number): string => {
    if (value <= 33) return "Léger";
    if (value <= 66) return "Modéré";
    return "Central";
  };

  const getStepFromSourceMatch = (value: number): number => {
    if (value > 66) {
      return value >= 90 ? 0 : 1;
    }
    if (value > 33) return 2;
    return value <= 5 ? 4 : 3;
  };

  const getSourceMatchFromStep = (step: number): number => {
    switch (step) {
      case 0: return 100;
      case 1: return 83;
      case 2: return 50;
      case 3: return 17;
      case 4: return 0;
      default: return 50;
    }
  };

  const formatSecondsLabel = (totalSeconds: number): string => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    const parts: string[] = [];
    if (mins > 0) {
      parts.push(`${mins} minute${mins > 1 ? "s" : ""}`);
    }
    if (secs > 0 || mins === 0) {
      parts.push(`${secs} seconde${secs > 1 ? "s" : ""}`);
    }
    return parts.join(" ");
  };

  const formatTimeMMSS = (totalSeconds: number): string => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const extractError = (err: unknown): string => {
    if (axios.isAxiosError(err)) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string" && detail.trim().length > 0) return detail;
      return err.message;
    }
    if (err instanceof Error) return err.message;
    return "Génération impossible.";
  };

  const handleGenerateClick = () => {
    if (isLocked) {
      setShowRegenerateConfirm(true);
      return;
    }
    void startGeneration(false);
  };

  const startGeneration = async (force: boolean) => {
    setShowRegenerateConfirm(false);
    if (!sessionId || !resolvedProjectName) {
      setGenerationError("Aucune session active : sélectionnez d'abord un projet.");
      return;
    }
    forcedRunRef.current = force;
    const specs: ScenarioSpec[] = scenarios.map((s) => ({
      prompt: s.prompt.trim(),
      audience: reverseFormatLabel(s.targetAudience, audienceOptions),
      tone: reverseFormatLabel(s.narrativeTone, toneOptions),
      target_duration: s.durationSeconds,
      source_usage_level: sourceUsageFromMatch(s.sourceMatch),
      tts_provider: "elevenlabs",
      tts_voice_id: ELEVEN_LABS_VOICES[0].id,
    }));
    const combinedPrompt = specs
      .map((s) => s.prompt)
      .filter((p) => Boolean(p))
      .join("\n\n");
    setGenerationError(null);
    setScenarioTarget(scenarios.length);
    queryClient.removeQueries({ queryKey: ["scenario-progress", sessionId] });
    setShowGenerationPopup(true);

    try {
      const projectAudio = await fetchProjectAudio(resolvedProjectName);

      if (!projectAudio || projectAudio.length === 0) {
        throw new Error(
          "Aucun fichier audio dans ce projet. Importez d'abord vos sources audio."
        );
      }
      await saveAudioSelection(sessionId, {
        project_name: resolvedProjectName,
        voices: projectAudio.slice(0, 3),
        backgrounds: { ambient: null, punctual: [] },
        auto_backgrounds: false,
        tts_provider: "elevenlabs",
        tts_voice_id: ELEVEN_LABS_VOICES[0].id,
      });

      await generateScenarios(
        sessionId,
        combinedPrompt,
        scenarios.length,
        "simple",
        undefined,
        specs,
        force
      );
      // Backend returns 202 immediately — generation runs in background.
      // Navigation is handled by onComplete once polling confirms all steps done.
    } catch (err) {
      setShowGenerationPopup(false);
      setGenerationError(extractError(err));
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1100px] min-h-[806px] overflow-y-auto p-6">
      <section className="w-full overflow-hidden rounded-[14px] border border-[#E2E8F0] bg-white shadow-[0_2px_10px_rgba(0,0,0,0.10)]">
        <div className="flex items-center gap-6 border-b border-[#F4F4F4] bg-[#F8FAFC] px-5 py-4">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="inline-flex items-center gap-2">
              <Settings className="h-5 w-5 text-[#0F172B]" />
              <h2 className="text-[20px] font-semibold leading-none text-[#0F172B]">Configuration des scénarios</h2>
            </div>
            <p className="text-[14px] font-medium leading-5 text-[#45556C]">
              Sélectionner plusieurs paramètres de générations pour vos scénarios.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-6 border-b border-[#F8FAFC] bg-white px-5 py-4">
          <div className="flex min-h-[38px] w-full flex-wrap items-center justify-end gap-3">
            {isLocked && (
              <div className="mr-auto flex min-w-0 max-w-full items-center gap-2 rounded-xl border border-[#FDE7C2] bg-[#FFFAF0] px-3 py-2">
                <Lock className="h-4 w-4 shrink-0 text-[#F59E0B]" />
                <span className="shrink-0 text-[13px] font-medium text-[#8A5A00]">Scénario retenu</span>
                {selectedScenarioTitle && (
                  <span className="truncate text-[13px] font-semibold text-[#0F172B]" title={selectedScenarioTitle}>
                    {selectedScenarioTitle}
                  </span>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={handleGenerateClick}
              disabled={!sessionId || selectedScenarioQuery.isLoading}
              className={`inline-flex h-[38px] shrink-0 items-center gap-1.5 rounded-xl px-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                isLocked
                  ? "border border-[#E2E8F0] bg-white text-[#0F172B] hover:bg-[#F8FAFC]"
                  : "bg-[#007AFF] text-white hover:bg-[#006ae0]"
              }`}
            >
              {isLocked ? <RotateCcw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
              <span>{isLocked ? "Regénérer" : "Générer"}</span>
            </button>
          </div>

          {scenarios.map((scenario) => (
            <ScenarioItemCard
              key={scenario.id}
              scenario={scenario}
              maxChars={maxChars}
              canDelete={scenarios.length > 1}
              showAiDropdown={showAiDropdownId === scenario.id}
              showAudienceDropdown={showAudienceDropdownId === scenario.id}
              showToneDropdown={showToneDropdownId === scenario.id}
              showVoiceDropdown={showVoiceDropdownId === scenario.id}
              displayAudienceOptions={displayAudienceOptions}
              displayToneOptions={displayToneOptions}
              voiceInstructions={voiceInstructions}
              playingVoiceId={playingVoiceId}
              loadingPreviewId={loadingPreviewId}
              onUpdate={updateScenario}
              onDelete={handleDeleteScenario}
              onToggleAiDropdown={(id) => setShowAiDropdownId(id)}
              onToggleAudienceDropdown={(id) => setShowAudienceDropdownId(id)}
              onToggleToneDropdown={(id) => setShowToneDropdownId(id)}
              onToggleVoiceDropdown={(id) => setShowVoiceDropdownId(id)}
              onVoiceInstructionsChange={setVoiceInstructions}
              onPlayPreview={handlePlayPreview}
            />
          ))}

          <div
            className={`flex h-[218px] w-full flex-col items-center justify-center gap-6 rounded-[18px] border border-dashed border-[#EFF6FF] bg-white p-[25px] ${scenarios.length >= 3 ? "opacity-60" : ""}`}
          >
            <button
              type="button"
              onClick={handleAddScenario}
              disabled={scenarios.length >= 3}
              className="inline-flex h-[52px] w-[52px] items-center justify-center rounded-[12px] bg-[#007AFF] text-white transition-colors hover:bg-[#006ae0] disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Ajouter un scénario"
            >
              <Plus className="h-7 w-7" />
            </button>
            <div className="flex flex-col items-center gap-2">
              <p className="text-center text-[16px] font-semibold leading-none text-[#45556C]">Ajouter un scénario</p>
              <p className="text-center text-[12px] font-normal leading-none text-[#8EA4BD]">
                Vous pouvez générer jusqu&apos;à 3 scénarios.
              </p>
            </div>
          </div>
        </div>
      </section>

      {generationError && (
        <div className="mt-4 rounded-lg border border-[#FF3B30] bg-[#fff1f0] px-4 py-3 text-sm text-[#FF3B30]">
          {generationError}
        </div>
      )}

      <ScenarioGenerationPopup
        open={showGenerationPopup}
        sessionId={sessionId}
        onClose={() => setShowGenerationPopup(false)}
        onComplete={() => {
          queryClient.invalidateQueries({ queryKey: ["scenarios", sessionId] });
          setShowGenerationPopup(false);
          if (forcedRunRef.current) {
            // Le backend a remis l'artefact à zéro après le choix de scénario.
            queryClient.invalidateQueries({ queryKey: ["selected-scenario", sessionId] });
            queryClient.invalidateQueries({ queryKey: ["project-artifacts", resolvedProjectName] });
            forcedRunRef.current = false;
            updateProgress({ scenariosReady: true, scenarioChosen: false, scenarioEdited: false });
          } else {
            updateProgress({ scenariosReady: true });
          }
          navigate("/step/choix_scenario");
        }}
        onError={(msg) => {
          setShowGenerationPopup(false);
          setGenerationError(msg);
        }}
        hideStatusCard={true}
      />

      <RegenerateScenariosDialog
        open={showRegenerateConfirm}
        onClose={() => setShowRegenerateConfirm(false)}
        onConfirm={() => void startGeneration(true)}
        scenarioTitle={selectedScenarioTitle}
        scenarioCount={scenarios.length}
      />
    </div>
  );
}

function formatOptionLabel(value: string) {
  if (!value || value === "Sélectionnez") return value || "Sélectionnez";
  return value
    .split("_")
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(" ");
}

function extractScenarioTitle(scenario: Record<string, unknown> | undefined | null): string | null {
  if (!scenario) return null;
  const payload = (scenario.scenario as Record<string, unknown> | undefined) ?? scenario;
  const structure = scenario.structure as Record<string, unknown> | undefined;
  const candidates = [payload.titre_global, structure?.titre_global, payload.titre, payload.title, scenario.titre];
  const title = candidates.find((value): value is string => typeof value === "string" && value.trim().length > 0);
  return title?.trim() ?? null;
}
